import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { kebabCase, uniqueId } from 'lodash-es';
import HBInput from '../HBInput/HBInput';
import HBMenu from '../HBMenu/HBMenu';
import {
    IMenuDivider,
    IMenuItem,
    ISection,
    TMenuItem
} from '../HBMenu/HBMenu.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { ComboboxItem, HBComboboxProps } from './HBCombobox.types';
import { buildComboboxItemTestIdName } from './HBCombobox.testIds';
import {
    COMBOBOX_MENU_CLASS,
    ComboboxMenuOverrides,
    StyledChevronEnd,
    StyledChevronIcon,
    StyledSearchWrapper,
    StyledTriggerText,
    StyledTriggerWrapper
} from './HBCombobox.styles';
import { semanticColors } from '../../styles/semanticColors';
import HBTooltip from '../HBTooltip/HBTooltip';
import { useHintedOverflowText } from '../common/internal/useHintedOverflowText';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';

function defaultFilter<T>(item: ComboboxItem<T>, query: string): boolean {
    const q = query.toLowerCase();
    return (
        item.label.toLowerCase().includes(q) ||
        (item.searchTerms?.some(t => t.toLowerCase().includes(q)) ?? false)
    );
}

function HBCombobox<T>({
    value,
    onChange,
    onOpenChange,
    sections,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    error = false,
    placeholder,
    disabled = false,
    size = 'sm',
    width,
    renderTriggerContent,
    showOverflowTooltip = false,
    searchPlaceholder = 'Search',
    searchFilter,
    emptyState = 'No results',
    menuMaxHeight = '300px',
    menuMaxWidth = '300px',
    menuMinWidth,
    menuWidth,
    menuPlacement = 'bottom',
    testId,
    testIdName
}: HBComboboxProps<T>) {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isTriggerFocused, setIsTriggerFocused] = useState(false);
    // Set right before the post-selection triggerRef.current.focus() call below,
    // so the resulting onFocus can tell "we just restored focus after closing
    // the menu" apart from a real user Tab/click into the trigger — otherwise
    // that restoration alone would pin the overflow tooltip open with no way
    // for the user to dismiss it.
    const isRestoringFocusRef = useRef(false);
    const {
        combineRefs: combineTriggerRefs,
        isOverflowing: isTriggerOverflowing
    } = useHintedOverflowText({
        isHintedEllipsis: showOverflowTooltip,
        ref: null
    });
    const searchInputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const listboxId = useRef(uniqueId('hb-combobox-listbox-')).current;
    const filter = searchFilter ?? defaultFilter;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    // Single funnel for every open-state transition (trigger, keyboard, selection,
    // Escape, outside click) so `onOpenChange` fires exactly once per transition.
    // Stable identity keeps the `menuItems` memo below from recomputing.
    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
        onOpenChangeRef.current?.(nextOpen);
    }, []);

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'combobox',
        componentName: 'HBCombobox'
    });

    const selectedItem = useMemo(
        () =>
            sections.flatMap(s => s.items).find(item => item.value === value) ??
            null,
        [sections, value]
    );

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                searchInputRef.current?.focus();
            });
        } else {
            setSearchQuery('');
        }
    }, [open]);

    const menuItems: TMenuItem[] = useMemo(() => {
        const result: TMenuItem[] = [];
        let hasItems = false;
        const isFiltering = !!searchQuery;
        const seen = new Set<unknown>();

        for (const section of sections) {
            const filtered = isFiltering
                ? section.items.filter(item => filter(item, searchQuery))
                : section.items;
            const deduped = isFiltering
                ? filtered.filter(item => {
                      if (seen.has(item.value)) {
                          return false;
                      }
                      seen.add(item.value);
                      return true;
                  })
                : filtered;
            if (!deduped.length) {
                continue;
            }

            if (hasItems) {
                result.push({ type: 'divider' } satisfies IMenuDivider);
            }

            if (!isFiltering && section.title) {
                result.push({
                    type: 'section',
                    sectionTitle: section.title
                } satisfies ISection);
            }
            for (const item of deduped) {
                const itemIdentifier = item.testIdName ?? kebabCase(item.label);
                result.push({
                    type: 'item',
                    icon: item.prefix,
                    title: item.label,
                    description: item.description,
                    rightIcon: item.rightContent,
                    selectable: 'single',
                    isSelected: value === item.value,
                    testIdName: buildComboboxItemTestIdName(
                        testIdName ?? 'combobox',
                        itemIdentifier
                    ),
                    onSelectionChange: () => {
                        onChangeRef.current(item.value);
                        handleOpenChange(false);
                        isRestoringFocusRef.current = true;
                        requestAnimationFrame(() => {
                            triggerRef.current?.focus();
                        });
                    }
                } satisfies IMenuItem);
                hasItems = true;
            }
        }

        if (!hasItems) {
            result.push({
                type: 'item',
                title: emptyState,
                disabled: true
            } satisfies IMenuItem);
        }

        return result;
    }, [
        sections,
        searchQuery,
        filter,
        value,
        emptyState,
        onChangeRef,
        handleOpenChange,
        testIdName
    ]);

    const triggerValue = selectedItem
        ? renderTriggerContent
            ? renderTriggerContent(selectedItem)
            : selectedItem.label
        : '';

    const triggerDisplayValue = triggerValue || placeholder || '';

    const triggerText = (
        <StyledTriggerText
            ref={combineTriggerRefs}
            $size={size}
            $hasValue={!!triggerValue}
            data-trigger-display=""
        >
            {triggerDisplayValue}
        </StyledTriggerText>
    );

    // Only the trigger's own text overflows via ellipsis, so hinted-ellipsis
    // tooltip support only needs to wrap this span — never the whole trigger.
    // openOnFocus is disabled and forceOpen driven manually instead: HBTooltip's
    // own focus detection listens on its TargetContainer (wrapping only this
    // span), but the real tabIndex/focusable element is StyledTriggerWrapper, an
    // ancestor of TargetContainer — focus landing there never bubbles down into
    // TargetContainer, so keyboard Tab would otherwise never open the tooltip
    // (WCAG 1.4.13 requires focus to reveal the same content hover does).
    const triggerContent = showOverflowTooltip ? (
        <HBTooltip
            forceClose={!isTriggerOverflowing}
            forceOpen={isTriggerFocused && isTriggerOverflowing}
            openOnFocus={false}
            placement="top"
            target={triggerText}
            targetContainerStyle={{
                display: 'flex',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden'
            }}
        >
            {triggerDisplayValue}
        </HBTooltip>
    ) : (
        triggerText
    );

    const trigger = (
        <StyledTriggerWrapper
            ref={triggerRef}
            id={id}
            role="combobox"
            aria-label={ariaLabelledBy ? undefined : ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-invalid={isAriaInvalid(error, ariaInvalid) || undefined}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            $disabled={disabled}
            $size={size}
            $width={width as string}
            data-testid={finalTestId}
            onFocus={() => {
                if (isRestoringFocusRef.current) {
                    isRestoringFocusRef.current = false;
                    return;
                }
                setIsTriggerFocused(true);
            }}
            onBlur={() => setIsTriggerFocused(false)}
            onKeyDown={e => {
                if (disabled) {
                    return;
                }
                if (
                    e.key === 'ArrowDown' ||
                    e.key === 'Enter' ||
                    e.key === ' '
                ) {
                    e.preventDefault();
                    handleOpenChange(true);
                }
                if (e.key === 'Escape') {
                    handleOpenChange(false);
                }
            }}
        >
            {triggerContent}
            <StyledChevronEnd aria-hidden="true">
                <StyledChevronIcon color={semanticColors.text.primary} />
            </StyledChevronEnd>
        </StyledTriggerWrapper>
    );

    return (
        <>
            <ComboboxMenuOverrides />
            <HBMenu
                target={trigger}
                targetAsChild={true}
                menuItems={menuItems}
                open={open}
                onOpenChange={handleOpenChange}
                placement={menuPlacement}
                align="start"
                maxHeight={menuMaxHeight}
                maxWidth={menuMaxWidth}
                minWidth={menuMinWidth}
                width={menuWidth}
                withArrow={false}
                preventFocusOnClose={true}
                disabled={disabled}
                className={COMBOBOX_MENU_CLASS}
                testIdName={testIdName ? `${testIdName}-menu` : 'combobox-menu'}
                slots={{
                    listHeader: () => (
                        <StyledSearchWrapper>
                            <HBInput
                                ref={searchInputRef}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const firstItem = menuItems.find(
                                            (item): item is IMenuItem =>
                                                item.type === 'item' &&
                                                !item.disabled
                                        );
                                        firstItem?.onSelectionChange?.(true);
                                    } else if (e.key.length === 1) {
                                        // Stop printable characters from bubbling to Radix's
                                        // typeahead handler, which would steal focus from the
                                        // search input and jump to a matching menu item.
                                        // Navigation keys (ArrowDown, Tab, Escape, etc.) have
                                        // key.length > 1 and are intentionally let through.
                                        e.stopPropagation();
                                    }
                                }}
                                placeholder={searchPlaceholder}
                                size="sm"
                                testIdName={
                                    testIdName
                                        ? `${testIdName}-search`
                                        : 'combobox-search'
                                }
                            />
                        </StyledSearchWrapper>
                    ),
                    listFooter: () => null
                }}
            />
        </>
    );
}

export default HBCombobox;
